import React, { Component } from 'react';
import update from 'immutability-helper';
import StepNav from './components/StepNav';
import OfficeInfo from './OfficeInfo';
import Panels from './Panels';
import BillingOptions from './BillingOptions';
import DiagnosisCodes from './DiagnosisCodes';
import SampleCollectionInfo from './SampleCollectionInfo';
import PatientInfo from './PatientInfo';
import Insurance from './Insurance';
import OrderConfirmation from './OrderConfirmation';
import ShipToOfficeConfirmation from './ship-to-office/ShipToOfficeConfirmation';
import PaymentServiceContainer from './PaymentServiceContainer';
import Route from './Route';
import SubmissionSummary from './SubmissionSummary';
import ShippingInformation from './ship-to-office/ShippingInformation';
import withWorkflow from './components/withWorkflow';
import withPricing from './components/withPricing';
import Breadcrumb from './components/Breadcrumb';
import workflowUtil from './util/workflowUtil';
import ConnectRememberMe from './phsycian-in-the-loop/rememberMeUtil';
import { isFullyAccessioned } from './util/gearOrderUtil';

/**
 * This component is the parent component for a GEAR order, containing common state and functions, and routing logic
 * for the order workflow steps.
 */
class Order extends Component {

	/**
	 * Constructor initializes state and binds functions.
	 *
	 * @param props the properties
	 */
	constructor(props) {
		super(props);

		this.state = {
			gearOrder: null,
			omsRequisition: null,
			activeGearRequisitionId: this.props.match.params.req || null,
			dirty: false,
			insuranceOptions: [],
			prices: [],
			addNewReq: false,
			doNotQuotePrices: false
		};

        this.updatePrices = this.updatePrices.bind(this);
        this.setBreadcrumbStep = this.setBreadcrumbStep.bind(this);
		this.fetchOrder = this.fetchOrder.bind(this);
		this.doConfirm = this.doConfirm.bind(this);
		this.setDirty = this.setDirty.bind(this);
		this.updatePractitionerInfo = this.updatePractitionerInfo.bind(this);
		this.setInsuranceOptions = this.setInsuranceOptions.bind(this);
		this.setGearOrder = this.setGearOrder.bind(this);
		this.setOmsRequisition = this.setOmsRequisition.bind(this);
		this.setActiveGearRequisitionId = this.setActiveGearRequisitionId.bind(this);
		this.setAddNewReq = this.setAddNewReq.bind(this);
	}

	/**
	 * Fetch the order when the component mounts if the user is logged in.
	 */
	componentDidMount() {
		if (this.props.userInfo !== null) {
			// If an order was passed through location state use it instead of fetching the order
			if (this.props.location?.state?.gearOrder) {
				this.setGearOrder(this.props.location.state.gearOrder);
			} else {
				this.fetchOrder();
			}
		}
        this.setBreadcrumbStep();
        this.updatePrices(null);
	}

    /**
     * Update the step in the breadcrumb and/or active requistiion if the route changed on component update.
     *
     * @param prevProps the previous properties
     * @param prevState the previous state
     */
    componentDidUpdate(prevProps, prevState) {
        if (this.props.location !== prevProps.location) {
            this.setBreadcrumbStep();
            if (this.props.match.params.req && this.props.match.params.req !== this.state.activeGearRequisitionId) {
                this.setState({
                    activeGearRequisitionId: this.props.match.params.req
                });
			}

			// Load the new order details if the order ID in the URL changes
			const isNewOrderPath = this.props.match.params.id === 'new';
			let routeOrderId = Number(this.props.match.params.id);
			if (isNewOrderPath || this.props.match.params.id === 'connect') {
				routeOrderId = 0;
			}
			const gearOrderId = this.state.gearOrder?.id || 0;

			// SCR-8162: If we have 'new' in the URL but we have a GearOrder in state, replace 'new' with the GearOrder ID
			if (gearOrderId !== 0 && isNewOrderPath && !this.state.gearOrder?.bundleTemplate) {
				let originalPath = this.props.location.pathname.replace('/new/', `/${gearOrderId}/`);
				this.props.history.replace(originalPath + this.props.location.search);
			} else if ((this.props.match.params.id === 'connect') && gearOrderId) {
				// If we have 'connect' in the URL and we already have a GearOrder in state, don't fetch the order
			} else if (this.props.userInfo !== null && routeOrderId !== gearOrderId) {
				this.fetchOrder();
			}
        }

        this.updatePrices(prevState);
    }

    /**
     * Updates the prices for the requisitions on the order.
     *
     * @param prevReqs the previous requisitions
     * @param prevInsurance the previous insurance
     */
	updatePrices(prevState) {
		// If the patient's address changes to or from 'Canada' add or remove the International Surcharge Fee
		const previousPatientCountry = prevState?.gearOrder?.patientInfo?.addresses?.find(address => address.type === 'shipping')?.country?.toUpperCase();
		const currentPatientCountry = this.state.gearOrder?.patientInfo?.addresses?.find(address => address.type === 'shipping')?.country?.toUpperCase();
		const paymentDate = this.state.gearOrder?.workflow?.steps?.find(step => step.id === 10 && step.completed)?.stepDate;

		const prevReqs = prevState?.gearOrder?.requisitions ?? null;
		const prevInsurance = prevState?.gearOrder?.insurance ?? null;
		const prevSecondaryInsurance = prevState?.gearOrder?.secondaryInsurance ?? null;

        if (this.state.gearOrder !== null && this.state.gearOrder.id > 0 && !this.state.doNotQuotePrices && !isFullyAccessioned(this.state.gearOrder)) {
            // first remove any prices that are no longer relevant (either from updated billing method or deleted req)
            if (this.state.prices.length > 0) {
                let updatedPrices = [];
                this.state.prices.forEach(price => {
                    let requisition = this.state.gearOrder.requisitions.find(req => price.gearRequisitionId === req.id && price.billingMethod === req.billingMethod);
                    if (requisition !== undefined &&
												((!this.state.gearOrder.insurance || (this.state.gearOrder.insurance && this.state.gearOrder.insurance.insuranceProvider && this.state.gearOrder.insurance.insuranceProvider === price.insuranceProvider))
												|| (!this.state.gearOrder.secondaryInsurance || (this.state.gearOrder.secondaryInsurance && this.state.gearOrder.secondaryInsurance.insuranceProvider && this.state.gearOrder.secondaryInsurance.insuranceProvider === price.insuranceProvider)))) {
                    	updatedPrices.push(price);
                    }
                });
                if (updatedPrices.length !== this.state.prices.length) {
                    this.setState({
                        prices: updatedPrices
                    });
                }
            }

            // then fetch any new/updated requisition prices
			this.state.gearOrder.requisitions.forEach(requisition => {
				// Do not fetch a price for accessioned PITL requisitions
				if (requisition.physicianInTheLoop && requisition.accessionNo) {
					return;
				}

				// determine if this is a new requisition/billing method
				let reqPrice = this.state.prices.find(price => price.gearRequisitionId === requisition.id);
				let newPrice = reqPrice === undefined && requisition.billingMethod !== null;

				// or if this is a requisition with an updated panels/components/addons
				let updatedPanels = false;
				let prevReq = prevReqs !== null && prevReqs.find(req => req.id === requisition.id);
				if (prevReq && (prevReq.panel !== requisition.panel || prevReq.components.toString() !== requisition.components.toString() || prevReq.addons.toString() !== requisition.addons.toString())) {
						updatedPanels = true;
				}

				// or if the insurance has been updated
				let insuranceChange = false;
				let prevProvider = (prevInsurance || {}).insuranceProvider ||  null;
				let prevSecondaryProvider = (prevSecondaryInsurance || {}).insuranceProvider || null;
				let newProvider = ((this.state.gearOrder || {}).insurance || {}).insuranceProvider || null;
				let newSecondaryProvider = ((this.state.gearOrder || {}).secondaryInsurance || {}).insuranceProvider || null;
				if (prevProvider !== newProvider) {
						insuranceChange = true;
				}
				if (prevSecondaryProvider !== newSecondaryProvider) {
						insuranceChange = true;
				}

				let pricingBillingMethod;
				if (this.state.gearOrder.insurance && (requisition.medicareTricareBillingSelected || requisition.insuranceBillingSelected)) {
					if ((newProvider !== "medicare" && newSecondaryProvider !== "medicare") &&
							(newProvider !== "medicaid" && newSecondaryProvider !== "medicaid") &&
							(newProvider !== "tricare" && newSecondaryProvider !== "tricare") &&
							(newProvider !== "medicare_railroad" && newSecondaryProvider !== "medicare_railroad")) {
						pricingBillingMethod = process.env.REACT_APP_BILLING_INSURANCE_VALUE;

						if((prevProvider !== "medicare" && prevSecondaryProvider !== "medicare") &&
								(prevProvider !== "medicaid" && prevSecondaryProvider !== "medicaid") &&
								(prevProvider !== "tricare" && prevSecondaryProvider !== "tricare") &&
								(prevProvider !== "medicare_railroad" && prevSecondaryProvider !== "medicare_railroad")) {
							//switched commercial carriers, do not need to re-query pricing
							insuranceChange = false;
						}
					} else if (newProvider === "medicaid" || newSecondaryProvider === "medicaid") {
						//pay-assist handles medicaid, but GEAR never passes this value, so update billing method based on ARBO logic for this payor.
						// Removed logic to redirect NC Medicaid to Medicare because contract has expired see HD-61073
						pricingBillingMethod = process.env.REACT_APP_BILLING_NO_INSURANCE_VALUE;

					} else if ((newProvider === "medicare" || newSecondaryProvider === "medicare" || newProvider === "tricare" || newSecondaryProvider === "tricare" || newProvider === "medicare_railroad" || newSecondaryProvider === "medicare_railroad")
							&& !requisition.medicareTricareBillingSelected) {
						//pass medicare and pay-assist calculates both medicare and tricare. the front end handles which payor pricing to display
						pricingBillingMethod = process.env.REACT_APP_BILLING_MEDICARE_VALUE;
					}  else {
						pricingBillingMethod = requisition.billingMethod;
					}
				} else {
					pricingBillingMethod = requisition.billingMethod;
				}

				if (newPrice || updatedPanels || insuranceChange) {
					// set a placeholder price in the state so the price is not re-fetched if the component updates before the pricing call returns
					this.setState((state, props) => {
						return {
							prices: state.prices.concat({
										gearRequisitionId: requisition.id,
										billingMethod: requisition.billingMethod,
										insuranceProvider: newProvider,
										secondaryInsuranceProvider: newSecondaryProvider,
										price: null,
										placeholder: true,
										error: false
									})
						};
					});

					this.props.getRequisitionPrice(requisition.id, pricingBillingMethod)
					.then(price => {
						if (price !== null) {
							// SCR-6588: Only use medicare or tricare price if insurance or medicare billing method is selected
							let orderInsuranceProvider = this.state.gearOrder?.insurance?.insuranceProvider;
							let orderSecondaryInsuranceProvider = this.state.gearOrder?.secondaryInsurance?.insuranceProvider;
							let isInsuranceBilling = requisition.billingMethod === process.env.REACT_APP_BILLING_MEDICARE_VALUE || requisition.billingMethod === process.env.REACT_APP_BILLING_INSURANCE_VALUE;
							if (isInsuranceBilling) {
								if ((orderInsuranceProvider === "medicare" || orderInsuranceProvider === "medicare_railroad" || orderSecondaryInsuranceProvider === "medicare" || orderSecondaryInsuranceProvider === "medicare_railroad") || (pricingBillingMethod === process.env.REACT_APP_BILLING_MEDICARE_VALUE && orderInsuranceProvider !== "tricare") || (pricingBillingMethod === process.env.REACT_APP_BILLING_MEDICARE_VALUE && orderSecondaryInsuranceProvider !== "tricare")) {
									price.price = price.medicarePrice;
									price.items = price.medicarePriceItems;
								} else if (orderInsuranceProvider === "tricare" || orderSecondaryInsuranceProvider === "tricare") {
									price.price = price.tricarePrice;
									price.items = price.tricarePriceItems;
								}
							}
							price.insuranceProvider = newProvider;
							price.secondaryInsuranceProvider = newSecondaryProvider;
							price.billingMethod = requisition.billingMethod;

							// If the GearRequisition has a saved discount apply it to the newly fetched price
							if (requisition?.paymentInfo?.discount) {
								// The saved discount doesn't include the orderableItemId. Add it for use in the calculate method.
								const discount = {
									...requisition.paymentInfo.discount,
									omsOrderableItemId: requisition.omsOrderableItemId
								}
								price = this.calculateAndAddDiscountToReqPrice(price, discount);
							}
							
							this.handleInternationalSurchargeFee(price, requisition, currentPatientCountry, paymentDate);
							this.setState((state, props) => {
								return {
									prices: state.prices
												.filter(oldPrice => oldPrice.gearRequisitionId !== requisition.id) // remove the placeholder or an old price, then add the fetched price
												.concat([price])
								};
							});

						}
					})
					.catch(response => {
						this.setState((state, props) => {
							return {
								prices: state.prices
											.filter(oldPrice => oldPrice.gearRequisitionId !== requisition.id) // remove the placeholder or an old price, then add the fetched price
											.concat({
												gearRequisitionId: requisition.id,
												billingMethod: requisition.billingMethod,
												insuranceProvider: newProvider,
												secondaryInsuranceProvider: newSecondaryProvider,
												price: null,
												placeholder: false,
												error: true
											})
							};
						});
					});
				}
				// We aren't fetching new prices but the patient country may have changed. Check if ISF needs to be added or removed.
				if (previousPatientCountry !== currentPatientCountry && (previousPatientCountry === 'CANADA' || currentPatientCountry === 'CANADA')) {
					this.setState((prevState) => {
						if (!prevState.prices?.length) {
							return null;
						}

						let updatedPrices = [];
						prevState.prices.forEach((price) => {
							const requisition = prevState.gearOrder?.requisitions?.find(
								(req) => price.gearRequisitionId === req.id && price.billingMethod === req.billingMethod
							);

							const updatedPrice = { ...price };
							this.handleInternationalSurchargeFee(updatedPrice, requisition, currentPatientCountry, paymentDate);
							updatedPrices.push(updatedPrice);
						});

						return {
							prices: updatedPrices
						};
					});
				}
            });
        }
	}

	/**
	 * Reloads the prices that have an errorr.
	 * updatePrices() is called on every update, so by removing the error prices from the state.prices list, 
	 * they will be refetched.
	 */
	reloadPrices = () => {
		this.setState(
			(state) => {
				return {
					prices: state.prices.filter((price) => !price.error && !price.configError)
				};
			}
		);
	}

	/**
	 * Apply a discount to the appropriate state.prices object.
	 *
	 * The correct price is found by matching the discount's omsOrderableItemId to a gearReq and
	 * then finding the price with the matching gearReqId.
	 *
	 * The discount amount is then calculated based on the discount type and the req price.
	 */
	applyDiscount = (discount) => {
		if (!discount?.discountAmount && discount?.discountType !== 'CLIENT' && discount?.discountType !== 'PRICE') {
			return;
		}

		/**
		 * Apply the discount to all reqs on the order if the order is PITL and the discount is a
		 * percentage discount and does not have a specific OmsRequisitionId.
		 */
		if (
			this.state.gearOrder.physicianInTheLoop &&
			this.state.gearOrder.requisitions?.length &&
			(discount.discountType === 'PERCENTAGE' || discount.discountType === 'AFFILIATE') &&
			!discount.omsOrderableItemId
		) {
			this.state.gearOrder.requisitions.forEach((gearReq, index) => {
				const discountedPrice = this.calculateAndAddDiscountToReqPrice(
					this.state.prices.find((price) => price.gearRequisitionId === gearReq.id),
					discount,
					this.state.gearOrder.physicianInTheLoop
				);

				if (discountedPrice.price && discountedPrice.discount?.discountAmount) {
					if (discount.discountType === 'AFFILIATE') {
						// Include the PITL physician fee in the first 'affiliate' discount
						// Compare to the price to prevent applying the same discount multiple times and getting -$15 each time.
						if (index === 0 && discountedPrice.discount.discountAmount <= discountedPrice.price ) {
							const physicianFee = discountedPrice?.items?.find(item => item?.name === "Physician Fee");
							discountedPrice.discount.discountAmount += physicianFee?.price || 0;
						}
						// Only include the shipping fee in the first 'affiliate' discount
						// The shipping fee was automatically included by calculateAndAddDiscountToReqPrice, so subtract it from all prices but the first
						if (index > 0) {
							const shippingFee = discountedPrice.items?.find(item => item.name === 'Shipping & Handling Fee')?.price || 0;
							discountedPrice.discount.discountAmount -= shippingFee;
						}
					}


					this.setState((state, props) => {
						return {
							prices: state.prices
								.filter((oldPrice) => oldPrice.gearRequisitionId !== gearReq.id) // remove the old price, then add the discounted price
								.concat([discountedPrice])
						};
					});
				}
			});
			return;
		}

		// Find the price for the matching omsOrderableItemId
		const gearReqId = this.state.gearOrder.requisitions?.find(gearReq => gearReq.omsOrderableItemId === discount?.omsOrderableItemId)?.id;
		const discountedPrice = this.calculateAndAddDiscountToReqPrice(this.state.prices.find(price => price.gearRequisitionId === gearReqId), discount, this.state.gearOrder.physicianInTheLoop);

		if ((discountedPrice.price && discountedPrice.discount?.discountAmount) || discountedPrice.discount?.discountType === 'CLIENT' || discountedPrice.discount?.discountType === 'PRICE') {
			this.setState((state, props) => {
				return {
					prices: state.prices
								.filter(oldPrice => oldPrice.gearRequisitionId !== gearReqId) // remove the old price, then add the discounted price
								.concat([discountedPrice])
				};
			});
		}
	}

	/**
	 * Calculate a discount amount based on the discount type and the req price and add the
	 * discount details to the price object.
	 * A new price object will be returned, the original object will not be modified.
	 */
	calculateAndAddDiscountToReqPrice = (reqPrice, discount, physicianInTheLoop) => {
		// Skip discount amount calculations for "Physician Account" discounts
		if (reqPrice?.billingMethod === process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE) {
			const discountedPrice = Object.assign({}, reqPrice);
			discountedPrice.discount = {
				discountCode: discount.discountCode,
				discountAmount: 0,
				discountType: discount.discountType,
				issueType: discount.issueType
			};

			return discountedPrice;
		}

		if (!reqPrice?.price || (!discount?.discountAmount && discount?.discountType !== 'PRICE')) {
			return reqPrice;
		}

		/*
		 * Clone the object with Object.assign so we don't modify the original price. This is
		 * important because PaymentInfo.js needs to be able to compare an old price to an updated
		 * price (componentDidUpdate needs to trigger when a discount is added to a price)
		 */
		const discountedPrice = Object.assign({}, reqPrice);

		let discountAmount;
		if (discount.discountType === 'PERCENTAGE' && physicianInTheLoop) {
			/* A PITL price contains the shipping fee but the discount does not get applied to the shipping fee.
			 * Subtract the shipping fee before calculating the discount amount.
			 */
			const shippingFee = discountedPrice.items?.find(item => item.name === 'Shipping & Handling Fee')?.price || 0;
			discountAmount = Math.max((discountedPrice.price - shippingFee), 0) * (discount.discountAmount / 100);
		} else if (discount.discountType === 'PERCENTAGE') {
			discountAmount = discountedPrice.price * (discount.discountAmount / 100);
		} else if (discount.discountType === 'PRICE') {
			discountAmount = discount.discountAmount;
		} else if (discount.discountType === 'AFFILIATE') {
			discountAmount = discountedPrice.price;
		} else {
			discountAmount = discount.discountAmount;
		}

		// Don't update the discount if an existing discount already has a higher amount
		if (discountedPrice?.discount?.discountAmount > discountAmount) {
			return discountedPrice;
		}

		discountedPrice.discount = {
			discountCode: discount.discountCode,
			discountAmount: discountAmount,
			discountType: discount.discountType,
			issueType: discount.issueType
		};

		return discountedPrice;
	}

	/**
	 * SCR-8403
	 * Add the International Surcharge Fee if:
	 *  - the shipping country is Canada
	 *  - the requisition is a drop-ship
	 *  - the billing method is not Physician
	 *  - the date is >= 1/8/2024
	 *  - there is no payment on the order, or the payment was made on or after 1/8/2024
	 * 
	 * Remove the fee if any of the above are false.
	 */
	handleInternationalSurchargeFee = (price, gearReq, shippingCountry, paymentDateStr) => {
		const isfDate = new Date(2024, 0, 8); // Jan 8, 2024
		const paymentDate = paymentDateStr && new Date(paymentDateStr);

		if (!price?.items || !gearReq || !shippingCountry || new Date() < isfDate || (paymentDate && paymentDate < isfDate)) {
			return;
		}

		const isfName = 'International Surcharge';
		const isfPrice = 30;

		const includeFee = shippingCountry.toUpperCase() === 'CANADA'
			&& gearReq.dropShip
			&& gearReq.billingMethod !== process.env.REACT_APP_BILLING_PHYSICIAN_ACCOUNT_VALUE;
		
		const hasFee = price.items.find(item => item.name === isfName);

		if (includeFee && !hasFee) {
			// Add fee to price items
			price.items.push({
				feeItem: false,
				name: isfName,
				price: isfPrice
			});

			// Add fee amount to prices
			price.price = (price.price || 0) + isfPrice;
			if (price.maxOutOfPocket || price.maxOutOfPocket === 0) {
				price.maxOutOfPocket += isfPrice;
			}
			if (price.medicarePrice || price.medicarePrice === 0) {
				price.medicarePrice += isfPrice;
			}
			if (price.tricarePrice || price.tricarePrice === 0) {
				price.tricarePrice += isfPrice;
			}
		} else if (!includeFee && hasFee) {
			// Remove fee from price items
			price.items = price.items.filter(item => item.name !== isfName);

			// Remove fee amount from prices
			price.price = Math.max(0, (price.price || 0) - isfPrice);
			if (price.maxOutOfPocket) {
				price.maxOutOfPocket = Math.max(0, price.maxOutOfPocket - isfPrice);
			}
			if (price.medicarePrice) {
				price.medicarePrice = Math.max(0, price.medicarePrice - isfPrice);
			}
			if (price.tricarePrice) {
				price.tricarePrice = Math.max(0, price.tricarePrice - isfPrice);
			}
		}
	}

    /**
     * Sets the workflow step in the breadcrumb.
     */
    setBreadcrumbStep() {
        this.props.setStep(Object.values(this.props.workflowMetaData).find(metaData => metaData.route === this.props.match.params.step)?.breadcrumb);
    }

	/**
	 * Sets a flag in state to signal that a new requisition is being added to an existing order.
	 *
	 * @param addNewReq true/false
	 */
	setAddNewReq( addNewReq ) {
		this.setState({addNewReq: addNewReq});
	}

	/**
	 * Fetches the GEAR order and sets the activeGearRequisitionId.
	 */
	fetchOrder() {
		if (!this.props.match.params.id) {
			return;
		}

		let orderId = this.props.match.params.id;
		let queryString = this.props.location.search;

		// When we have existing PITL order details, pass the details for validation and possibly to update an exising order
		const previousPitlDetails = ConnectRememberMe.getStoredValue();
		if (ConnectRememberMe.isReturningVisit(orderId)) {
			ConnectRememberMe.refresh();
			if (!queryString?.includes('dateOfBirth')) {
				queryString = (queryString ? queryString + '&' : '?') + 'dateOfBirth=' + previousPitlDetails.dateOfBirth;
			}

			// 'merge' will signal the back-end to add the new product(s) to the existing order (If they're not already on the order)
			if (orderId === 'connect') {
				queryString += '&merge=' + previousPitlDetails.gearOrderId;
			}
		}

		this.props.setLoading(true);
		fetch(process.env.REACT_APP_GEAR_REST_URL + "/order/" + orderId + queryString, {
			mode: 'cors',
			credentials: 'include',
			method: 'GET',
			timeout: 1000,
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json'
			}
		})
		.then(response => {
			if (response.ok) {
				this.props.setLoading(false);
				return response.json();
			} else {
				return Promise.reject({
					response: response
				});
			}
		})
		.then(json => {
			let activeGearRequisitionId = this.props.match.params.req || null;
			if ( json && json.id && json.requisitions && json.requisitions.length > 0 && activeGearRequisitionId === null ) {
				// Find the first requisition from the workflow steps
				if( json.workflow && json.workflow.steps ) {
					let firstReqStep = json.workflow.steps.find( function( step ){ return step.gearReqId});
					if( firstReqStep ) {
						activeGearRequisitionId = firstReqStep.gearReqId;
					}
				}

				// Couldn't find a requisition from the workflow steps. Default to the last req created (highest req id)
				if( !activeGearRequisitionId ) {
					activeGearRequisitionId = Math.max.apply(Math, json.requisitions.map( req => {
						if( !isNaN( req.id ) ) {
							return req.id;
						}
						// Default to 0 for undefined id values
						return 0;
					} ) );
				}
			}
			this.setState({
				activeGearRequisitionId: activeGearRequisitionId
			});
			this.setGearOrder(json);

			/**
			 * If an existing order was loaded while on the /connect URL, forward the user to the confirmation page.
			 * This will happen when a user returns to Gear after PITL remember me details have been saved.
			 */
			if (this.props.match.params.id === 'connect' && json?.id) {
				this.props.updateStep(json, 'patient_confirmation');
			}
		})
		.catch(e => {
			this.props.setLoading(false);
			if (e.response && e.response.status === 404) {
				this.props.showError("The order was not found.");
			} else {
				this.props.showError("The network is currently unavailable. Please call us at 800-522-4762.");
			}
		});
	}

	/**
	 * Prompts the user to confirm they want to leave the current step with unsaved changes.
	 *
	 * @param e the DOM event
	 */
	doConfirm(e) {
       if (this.state.dirty) {
           if (!window.confirm('You will lose any unsaved changes. To save, Cancel this and click Save below. Click OK to discard changes.')) {
               e.preventDefault();
               return false;
           }
       }
       this.setDirty(false);
       return true;
    }

	/**
	 * Sets the 'dirty' state.
	 *
	 * @param isDirty whether the step has been changed
	 */
	setDirty(isDirty) {
        this.setState({
          dirty: isDirty
        });
    }

	/**
	 * Updates the practtioner information in component state. The practitioner info will not be saved on a new order
	 * until a panel has been added to the order. Also updates the workflow to indicate the practitioner step has been
	 * completed.
	 *
	 * @param practitioner the chosen practitioner
	 * @param practAgreement whether the practitioner agreement was checked
	 */
	updatePractitionerInfo(practitioner, practAgreement, orderSource) {
		let myGDXDropShip = (orderSource === 'kitOrdering' ? true : false);
		let updatedOrder = update(this.state.gearOrder, {
			practAgreement: { $set: practAgreement },
			practitioner: { $set: practitioner },
			workflow: {
				steps: {
					0: {
						notStarted: { $set: false },
						completed: { $set: true },
						status: {
							id: { $set: 3 },
							name: { $set: "Completed" }
						}
					}
				}
			},
			myGDXKitOrder : { $set: myGDXDropShip }
		});
		this.setGearOrder(updatedOrder);
	}

	/**
	 * Sets the insurance options found from a patient search.
	 *
	 * @param options the options to set
	 */
	setInsuranceOptions(options) {
  	  this.setState({
  		  insuranceOptions: options
  	  });
    }

	/**
	 * Sets an updated GEAR order in state.
	 *
	 * @param order the updated GEAR order
	 */
	setGearOrder(order) {
		// Don't get prices for prc users when practitioner has "Do Not Quote Prices to Patients" attribute.
		const doNotQuotePrices =
			this.props.userInfo &&
			this.props.userInfo.type === 'prc' &&
			order &&
			order.practitioner &&
			order.practitioner.doNotQuotePrices;

		this.setState({
			gearOrder: order,
			doNotQuotePrices: doNotQuotePrices
		});

		if (window.replaceHeader) {
			window.replaceHeader(order?.physicianInTheLoop ?? false);
		}
	}

	/**
	 * Sets an updated omsRequisition in state.
	 *
	 * @param omsRequisition the updated omsRequisition
	 */
	setOmsRequisition( omsRequisition ) {
		this.setState({
			omsRequisition: omsRequisition
		});
	}

	/**
	 * Sets the active requisition ID
	 *
	 * @param requisitionNumber the number of the requisition to set as active
	 */
	setActiveGearRequisitionId( requisitionNumber ) {
		this.setState({
			activeGearRequisitionId: requisitionNumber
		});
	}

	/**
	 * Renders the component.
	 */
	render() {
		let routeOrderId = Number(this.props.match.params.id);
		if (this.props.match.params.id === 'new' || this.props.match.params.id === 'connect') {
			routeOrderId = 0;
		}
		const gearOrderId = this.state.gearOrder?.id || 0;

		/*
		 * Don't display anything if the order ID in the URL doesn't match the state's gearOrderId.
		 * A fetch is in progress and the order will be displayed when the fetch returns
		 *  (because the state's gearOrderId will have been updated to match the URL)
		 */
		if (!this.props.userInfo || !this.state.gearOrder || (routeOrderId !== gearOrderId && this.props.match.params.id !== 'connect')) {
			return null;
		}

		let routeComponent = null;
		let step = this.props.match.params.step;

		// physicianInTheLoop orders might start without a path specified. Use the first workflow step on the order to find the correct path.
		if(!step && this.state.gearOrder?.physicianInTheLoop) {
			const firstOrderStepId = this.state.gearOrder?.workflow?.steps?.[0]?.id;
			step = firstOrderStepId && workflowUtil.getWorkflowMetaDataById(firstOrderStepId)?.route;
		}

		switch (step) {
			case "pract":
				routeComponent = <OfficeInfo
					userInfo={this.props.userInfo}
					gearOrder={this.state.gearOrder}
					doConfirm={this.doConfirm}
					setDirty={this.setDirty}
					showError={this.props.showError}
					updatePractitionerInfo={this.updatePractitionerInfo}
					setGearOrder={this.setGearOrder}
				/>;
				break;
			case "panels":
				routeComponent = <Panels
					userInfo={this.props.userInfo}
					gearOrder={this.state.gearOrder}
					omsRequisition={this.state.omsRequisition}
					activeGearRequisitionId={this.state.activeGearRequisitionId}
					doConfirm={this.doConfirm}
					setDirty={this.setDirty}
					showError={this.props.showError}
					setLoading={this.props.setLoading}
					setGearOrder={this.setGearOrder}
					setOmsRequisition={this.setOmsRequisition}
					setActiveGearRequisitionId={this.setActiveGearRequisitionId}
					step={step}
					addNewReq={this.state.addNewReq}
					setAddNewReq={this.setAddNewReq}
					key={this.state.activeGearRequisitionId}
				/>;
				break;
			case "billing":
				routeComponent = <BillingOptions
					userInfo={this.props.userInfo}
					gearOrder={this.state.gearOrder}
					omsRequisition={this.state.omsRequisition}
					activeGearRequisitionId={this.state.activeGearRequisitionId}
					setDirty={this.setDirty}
					doConfirm={this.doConfirm}
					showError={this.props.showError}
					setLoading={this.props.setLoading}
					setGearOrder={this.setGearOrder}
					setOmsRequisition={this.setOmsRequisition}
					setActiveGearRequisitionId={this.setActiveGearRequisitionId}
					step={step}
					doNotQuotePrices={this.state.doNotQuotePrices}
					 />;
				 break;
			case "dxCodes":
				routeComponent = <DiagnosisCodes
					userInfo={this.props.userInfo}
					gearOrder={this.state.gearOrder}
					omsRequisition={this.state.omsRequisition}
					activeGearRequisitionId={this.state.activeGearRequisitionId}
					setDirty={this.setDirty}
					doConfirm={this.doConfirm}
					showError={this.props.showError}
					setLoading={this.props.setLoading}
					setGearOrder={this.setGearOrder}
					setOmsRequisition={this.setOmsRequisition}
					setActiveGearRequisitionId={this.setActiveGearRequisitionId}
					step={step}
				/>;
				break;
			case "collection":
				routeComponent = <SampleCollectionInfo
					userInfo={this.props.userInfo}
					gearOrder={this.state.gearOrder}
					omsRequisition={this.state.omsRequisition}
					activeGearRequisitionId={this.state.activeGearRequisitionId}
					setDirty={this.setDirty}
					doConfirm={this.doConfirm}
					showError={this.props.showError}
					setLoading={this.props.setLoading}
					setGearOrder={this.setGearOrder}
					setOmsRequisition={this.setOmsRequisition}
					setActiveGearRequisitionId={this.setActiveGearRequisitionId}
					step={step}
					setAddNewReq={this.setAddNewReq}
					key={this.state.activeGearRequisitionId}
				/>;
				break;
			case "patient":
				routeComponent = <PatientInfo
					userInfo={this.props.userInfo}
					gearOrder={this.state.gearOrder}
					doConfirm={this.doConfirm}
					setDirty={this.setDirty}
					showError={this.props.showError}
					insuranceOptions={this.state.insuranceOptions}
					setInsuranceOptions={this.setInsuranceOptions}
					setLoading={this.props.setLoading}
					setGearOrder={this.setGearOrder}
				/>;
				break;
			case "insurance":
				routeComponent = <Insurance
					userInfo={this.props.userInfo}
					gearOrder={this.state.gearOrder}
					doConfirm={this.doConfirm}
					setDirty={this.setDirty}
					showError={this.props.showError}
                    setInsuranceOptions={this.setInsuranceOptions}
					options={this.state.insuranceOptions}
					setGearOrder={this.setGearOrder}
				/>;
				break;
			case "confirm":
				if( this.state.gearOrder && this.state.gearOrder.containsShipToOfficeRequisition ) {
					routeComponent = <ShipToOfficeConfirmation
						userInfo={this.props.userInfo}
						gearOrder={this.state.gearOrder}
						doConfirm={this.doConfirm}
						setDirty={this.setDirty}
						showError={this.props.showError}
						setLoading={this.props.setLoading}
						setGearOrder={this.setGearOrder}
	                    setOmsRequisition={this.setOmsRequisition}
	                    setActiveGearRequisitionId={this.setActiveGearRequisitionId}
	                    prices={this.state.prices}
					/>;
				} else {
					routeComponent = <OrderConfirmation
						userInfo={this.props.userInfo}
						gearOrder={this.state.gearOrder}
						doConfirm={this.doConfirm}
						setDirty={this.setDirty}
						showError={this.props.showError}
						setLoading={this.props.setLoading}
						setGearOrder={this.setGearOrder}
	                    setOmsRequisition={this.setOmsRequisition}
	                    setActiveGearRequisitionId={this.setActiveGearRequisitionId}
						prices={this.state.prices}
						applyDiscount={this.applyDiscount}
						setAddNewReq={this.setAddNewReq}
						doNotQuotePrices={this.state.doNotQuotePrices}
						insuranceOptions={this.state.insuranceOptions}
						setInsuranceOptions={this.setInsuranceOptions}
						reloadPrices={this.reloadPrices}
						slowFetch={this.props.slowFetch}
					/>;
				}
				break;
            case "payment":
				routeComponent = <PaymentServiceContainer
					userInfo={this.props.userInfo}
					gearOrder={this.state.gearOrder}
					doConfirm={this.doConfirm}
					setDirty={this.setDirty}
					showError={this.props.showError}
					setLoading={this.props.setLoading}
					setGearOrder={this.setGearOrder}
					prices={this.state.prices}
					applyDiscount={this.applyDiscount}
					doNotQuotePrices={this.state.doNotQuotePrices}
				/>;
				break;
			case "handoff":
				routeComponent = <Route
					userInfo={this.props.userInfo}
					gearOrder={this.state.gearOrder}
					doConfirm={this.doConfirm}
					setGearOrder={this.setGearOrder}
					setLoading={this.props.setLoading}
				/>
				break;
			case "summary":
				routeComponent = <SubmissionSummary
					userInfo={this.props.userInfo}
					gearOrder={this.state.gearOrder}
					doConfirm={this.doConfirm}
					setLoading={this.props.setLoading}
					setGearOrder={this.setGearOrder}
				/>;
				break;
			case "shipping":
				routeComponent = <ShippingInformation
					userInfo={this.props.userInfo}
					gearOrder={this.state.gearOrder}
					doConfirm={this.doConfirm}
					setDirty={this.setDirty}
					showError={this.props.showError}
					setGearOrder={this.setGearOrder}
					prices={this.state.prices}
					setLoading={this.props.setLoading}
				/>;
				break;
			case "registration": // PRC Practitioner Confirmation
				routeComponent = <OrderConfirmation
					userInfo={this.props.userInfo}
					gearOrder={this.state.gearOrder}
					doConfirm={this.doConfirm}
					setDirty={this.setDirty}
					showError={this.props.showError}
					setLoading={this.props.setLoading}
					setGearOrder={this.setGearOrder}
					setOmsRequisition={this.setOmsRequisition}
					setActiveGearRequisitionId={this.setActiveGearRequisitionId}
					prices={this.state.prices}
					practitionerSummary={true}
					setAddNewReq={this.setAddNewReq}
				/>;
				break;
			default:
				routeComponent = null; //TODO: show a 404 view?
		}

		let bgImageStyle = {backgroundImage: 'none'};
        let bgClassName = "";
		bgImageStyle = {backgroundColor: 'white'};

		let activeReqName = '';
		if( this.state.gearOrder && this.state.activeGearRequisitionId && this.state.gearOrder.requisitions ) {
			let activeReq = this.state.gearOrder.requisitions.find( (item) => {
				return item.id === this.state.activeGearRequisitionId;
			});
			if( activeReq ) {
				activeReqName = activeReq.omsOrderableItemName;
			}
		}

		return (
			<div>
				<Breadcrumb
					gearOrder={this.state.gearOrder}
					breadcrumbStep={this.props.breadcrumbStep}
					gearReqId={this.state.activeGearRequisitionId}
					reqName={activeReqName}
					setGearOrder={this.setGearOrder}
					setOmsRequisition={this.setOmsRequisition}
					setActiveGearRequisitionId={this.setActiveGearRequisitionId}
					showError={this.props.showError}
					userInfo={this.props.userInfo}
					prices={this.state.prices}
					setAddNewReq={this.setAddNewReq}
					doNotQuotePrices={this.state.doNotQuotePrices}
				/>
				<div className={"banner section " + bgClassName} style={bgImageStyle}>
					<div className="container">
						<StepNav
							userInfo={this.props.userInfo}
							gearOrder={this.state.gearOrder}
							doConfirm={this.doConfirm}
						/>
						{routeComponent}
					</div>
				</div>
			</div>
		);
	}
}

export default withPricing(withWorkflow(Order));
